// THE search-text normalizer — lowercase + strip combining diacritics so
// "Curl français" matches "francais". Extracted from the two byte-identical
// inline copies (ExercisePicker, catalogSections) when the Library browser
// became the third caller (HR-S3c).
export const normalizeSearch = (s: string): string =>
  s.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");

// ── Exercise search (#361) ────────────────────────────────────────────
// One matcher for every exercise list and GET /api/exercises?q=.
// Query tokens (any order) must each be found in the haystack: names in all
// locales, aliases, slug words. The raw token matches by substring (German
// compounds); its plural stem (see `stem`) matches only a whole word.
const tokenize = (s: string): string[] =>
  normalizeSearch(s)
    .split(/[^\p{L}\p{N}]+/u)
    .filter((t) => t.length > 0);

// Plural tolerance: a token of 4+ chars also stands for itself minus a final
// "s", "es" or "x" (whichever apply). Forms must intersect: whole word only.
const forms = (tok: string): string[] => {
  if (tok.length < 4) return [tok];
  const out = [tok];
  if (tok.endsWith("s")) out.push(tok.slice(0, -1));
  if (tok.endsWith("es")) out.push(tok.slice(0, -2));
  if (tok.endsWith("x")) out.push(tok.slice(0, -1));
  return out;
};

// 0 = a token is nowhere; 1 = all contained; 2 = all prefix or whole word.
const tokensMatch = (qTokens: string[], hay: string[]): 0 | 1 | 2 => {
  const hayForms = new Set(hay.flatMap(forms));
  let prefix = true;
  for (const q of qTokens) {
    if (hay.some((h) => h.startsWith(q))) continue;
    if (forms(q).some((f) => hayForms.has(f))) continue;
    prefix = false;
    if (!hay.some((h) => h.includes(q))) return 0;
  }
  return prefix ? 2 : 1;
};

export type SearchableExercise = {
  slug: string;
  name: Partial<Record<"en" | "it" | "fr" | "de" | "ru", string | undefined>>;
  aliases?: readonly string[] | undefined;
};

const LOCALES = ["en", "it", "fr", "de", "ru"] as const;

/** 0 = no match, else higher is better: 100 exact UI-locale name, 80 it
 *  starts with the query, 60 all tokens in it, 40 in another locale's name,
 *  20 alias or slug only; prefix hits get +5 over mid-word. Empty query: 1. */
export function matchExercise(
  ex: SearchableExercise,
  query: string,
  locale: string = "en",
): number {
  const qTokens = tokenize(query);
  if (qTokens.length === 0) return 1;
  const uiName = ex.name[locale as (typeof LOCALES)[number]] ?? ex.name.en;
  const uiTokens = tokenize(uiName ?? "");
  const q = qTokens.join(" ");
  if (uiTokens.join(" ") === q) return 100;
  const ui = tokensMatch(qTokens, uiTokens);
  if (ui > 0) {
    if (uiTokens.join(" ").startsWith(q)) return 80;
    return ui === 2 ? 65 : 60;
  }
  const names = LOCALES.flatMap((l) => tokenize(ex.name[l] ?? ""));
  const other = tokensMatch(qTokens, names);
  if (other > 0) return other === 2 ? 45 : 40;
  const rest = [
    ...names,
    ...(ex.aliases ?? []).flatMap(tokenize),
    ...tokenize(ex.slug),
  ];
  const r = tokensMatch(qTokens, rest);
  return r === 0 ? 0 : r === 2 ? 25 : 20;
}

/** Filter by match and sort best first; `tieBreak` orders equal scores (and
 *  everything, when the query is empty). Each score is computed once. */
export function rankByMatch<T extends SearchableExercise>(
  items: readonly T[],
  query: string,
  locale: string | undefined,
  tieBreak: (a: T, b: T) => number,
): T[] {
  const q = query.trim();
  if (q === "") return [...items].sort(tieBreak);
  return items
    .map((item) => ({ item, score: matchExercise(item, q, locale) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || tieBreak(a.item, b.item))
    .map((r) => r.item);
}
